import React, { forwardRef, useState, useMemo } from 'react';
import { parsePhoneNumber, getCountryCallingCode } from 'react-phone-number-input';
import en from 'react-phone-number-input/locale/en';
import 'react-phone-number-input/style.css';
import { Input } from './input';
import { cn } from '../../lib/utils';
import { Button } from './button';
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from './command';
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from './popover';
import { Check, ChevronsUpDown } from 'lucide-react';

// Priority countries - shown at the top
const PRIORITY_COUNTRIES = ['TZ', 'UG', 'MW'];

// Common countries to show initially (before searching)
const COMMON_COUNTRIES = ['TZ', 'UG', 'MW', 'KE', 'RW', 'ZA', 'NG', 'GH', 'US', 'GB'];

// All supported countries
const ALL_COUNTRIES = [
  'TZ', 'UG', 'MW', 'KE', 'RW', 'ET', 'ZA', 'NG', 'GH', 'EG', 'MA', 'DZ', 'TN',
  'US', 'GB', 'CA', 'AU', 'NZ', 'DE', 'FR', 'IT', 'ES', 'PT', 'NL', 'BE', 'CH',
  'AT', 'SE', 'NO', 'DK', 'FI', 'PL', 'CZ', 'HU', 'RO', 'BG', 'GR', 'TR', 'RU',
  'UA', 'IN', 'PK', 'BD', 'CN', 'JP', 'KR', 'ID', 'MY', 'SG', 'TH', 'VN', 'PH',
  'AE', 'SA', 'QA', 'KW', 'BH', 'OM', 'JO', 'LB', 'IL', 'BR', 'AR', 'CL', 'CO',
  'PE', 'VE', 'MX', 'ZW', 'BW', 'NA', 'ZM', 'MZ', 'AO', 'CD', 'CM', 'CI', 'SN',
];

// Country flag component using emoji flags
const CountryFlag = ({ country }) => {
  const getFlagEmoji = (countryCode) => {
    const codePoints = countryCode
      .toUpperCase()
      .split('')
      .map(char => 127397 + char.charCodeAt(0));
    return String.fromCodePoint(...codePoints);
  };
  
  return (
    <span className="text-lg leading-none" role="img" aria-label={country}>
      {getFlagEmoji(country)}
    </span>
  );
};

/**
 * PhoneInput - A phone number input with searchable country selector
 */
const PhoneInput = ({ 
  value, 
  onChange, 
  placeholder = "Enter phone number",
  disabled = false,
  className = "",
  defaultCountry = "TZ",
  id,
  "data-testid": dataTestId,
  required = false,
}) => {
  const [open, setOpen] = useState(false);
  const [selectedCountry, setSelectedCountry] = useState(defaultCountry);
  const [searchQuery, setSearchQuery] = useState('');
  const [phoneNumber, setPhoneNumber] = useState('');

  // Parse existing value to extract country and number
  useMemo(() => {
    if (value) {
      try {
        const parsed = parsePhoneNumber(value);
        if (parsed) {
          setSelectedCountry(parsed.country || defaultCountry);
          setPhoneNumber(parsed.nationalNumber || '');
        }
      } catch (e) {
        // If parsing fails, keep current state
      }
    }
  }, [value, defaultCountry]);

  // Get country calling code
  const getCallingCode = (country) => {
    try {
      return getCountryCallingCode(country);
    } catch {
      return '';
    }
  };

  // Handle phone number change
  const handlePhoneChange = (e) => {
    const newNumber = e.target.value.replace(/[^\d]/g, '');
    setPhoneNumber(newNumber);
    
    const callingCode = getCallingCode(selectedCountry);
    if (callingCode && newNumber) {
      onChange(`+${callingCode}${newNumber}`);
    } else {
      onChange('');
    }
  };

  // Handle country change
  const handleCountryChange = (country) => {
    setSelectedCountry(country);
    setOpen(false);
    setSearchQuery('');
    
    const callingCode = getCallingCode(country);
    if (callingCode && phoneNumber) {
      onChange(`+${callingCode}${phoneNumber}`);
    }
  };

  // Filter and sort countries based on search
  const filteredCountries = useMemo(() => {
    const query = searchQuery.toLowerCase();
    
    if (!query) {
      // Show common countries when not searching
      return COMMON_COUNTRIES;
    }
    
    // Filter all countries by name or code
    return ALL_COUNTRIES.filter(code => {
      const name = en[code] || code;
      return (
        name.toLowerCase().includes(query) ||
        code.toLowerCase().includes(query)
      );
    }).sort((a, b) => {
      // Priority countries first
      const aIsPriority = PRIORITY_COUNTRIES.includes(a);
      const bIsPriority = PRIORITY_COUNTRIES.includes(b);
      if (aIsPriority && !bIsPriority) return -1;
      if (!aIsPriority && bIsPriority) return 1;
      
      // Then alphabetically by name
      const nameA = en[a] || a;
      const nameB = en[b] || b;
      return nameA.localeCompare(nameB);
    });
  }, [searchQuery]);

  const callingCode = getCallingCode(selectedCountry);
  const countryName = en[selectedCountry] || selectedCountry;

  return (
    <div 
      className={cn(
        "flex items-center gap-2",
        disabled && "opacity-50",
        className
      )}
      data-testid={dataTestId}
    >
      {/* Country Selector */}
      <Popover open={open} onOpenChange={setOpen}>
        <PopoverTrigger asChild>
          <Button
            variant="outline"
            role="combobox"
            aria-expanded={open}
            className="w-auto px-3 justify-between gap-2"
            disabled={disabled}
          >
            <CountryFlag country={selectedCountry} />
            <span className="text-sm text-muted-foreground">+{callingCode}</span>
            <ChevronsUpDown className="h-3 w-3 shrink-0 opacity-50" />
          </Button>
        </PopoverTrigger>
        <PopoverContent className="w-[280px] p-0" align="start">
          <Command shouldFilter={false}>
            <CommandInput 
              placeholder="Search country..." 
              value={searchQuery}
              onValueChange={setSearchQuery}
            />
            <CommandList>
              <CommandEmpty>No country found.</CommandEmpty>
              
              {/* Priority Countries Group */}
              {!searchQuery && (
                <CommandGroup heading="Suggested">
                  {PRIORITY_COUNTRIES.map((code) => (
                    <CommandItem
                      key={code}
                      value={code}
                      onSelect={() => handleCountryChange(code)}
                      className="flex items-center gap-2"
                    >
                      <Check
                        className={cn(
                          "h-4 w-4",
                          selectedCountry === code ? "opacity-100" : "opacity-0"
                        )}
                      />
                      <CountryFlag country={code} />
                      <span className="flex-1">{en[code]}</span>
                      <span className="text-muted-foreground text-sm">+{getCallingCode(code)}</span>
                    </CommandItem>
                  ))}
                </CommandGroup>
              )}
              
              {/* Other Countries */}
              <CommandGroup heading={searchQuery ? "Results" : "Other countries"}>
                {filteredCountries
                  .filter(code => searchQuery || !PRIORITY_COUNTRIES.includes(code))
                  .map((code) => (
                    <CommandItem
                      key={code}
                      value={code}
                      onSelect={() => handleCountryChange(code)}
                      className="flex items-center gap-2"
                    >
                      <Check
                        className={cn(
                          "h-4 w-4",
                          selectedCountry === code ? "opacity-100" : "opacity-0"
                        )}
                      />
                      <CountryFlag country={code} />
                      <span className="flex-1">{en[code]}</span>
                      <span className="text-muted-foreground text-sm">+{getCallingCode(code)}</span>
                    </CommandItem>
                  ))}
              </CommandGroup>
            </CommandList>
          </Command>
        </PopoverContent>
      </Popover>

      {/* Phone Number Input */}
      <Input
        id={id}
        type="tel"
        value={phoneNumber}
        onChange={handlePhoneChange}
        placeholder={placeholder}
        disabled={disabled}
        className="flex-1"
        required={required}
      />
    </div>
  );
};

export { PhoneInput };
